import React, { useState } from 'react';

import { useInRouterContext } from 'react-router-dom';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Alert, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Menu, MenuItem } from '@mui/material';

import type { PrintSettings } from '~/pages/LeLens/print/printSettings';
import LensSchedulesDialog from '~/pages/LeLens/schedules/LensSchedulesDialog';
import { useReportAction } from '~/pages/LeLens/useReportAction';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { LensFilterValues, LensPeriod } from '~/types/leLens';

import NativePdfSettingsDialog from './NativePdfSettingsDialog';
import { nativeLensId } from './nativeLensId';

type Props = {
  pageId: string;
  filters: LensFilterValues;
  filterOptions: Record<string, Array<string>>;
  ready: boolean;
  onDownloadPdf: (settings?: PrintSettings) => void;
  initialPeriod?: LensPeriod | null;
  allowScheduling?: boolean;
  /** The report renders its own PDF export, so the menu leaves Download PDF out. */
  hasOwnPdfExport?: boolean;
};

// PDF exports also render these controls without an application router.
function ScheduleHandoff({ ready, onOpen }: { ready: boolean; onOpen: () => void }) {
  useReportAction('schedules', onOpen, ready);
  return null;
}

export default function NativeLensActions({
  pageId,
  filters,
  filterOptions,
  ready,
  onDownloadPdf,
  initialPeriod,
  allowScheduling = true,
  hasOwnPdfExport = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const { user } = useCurrentUser();
  const query = useGetReportingLeLens(open ? nativeLensId(pageId) : undefined);
  const routed = useInRouterContext();
  if (hasOwnPdfExport && !allowScheduling) {
    return null;
  }
  return (
    <>
      {routed && <ScheduleHandoff ready={allowScheduling} onOpen={() => setOpen(true)} />}
      <IconButton aria-label="Report actions" aria-haspopup="menu" onClick={(event) => setAnchor(event.currentTarget)}>
        <MoreHorizIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {!hasOwnPdfExport && (
          <MenuItem
            disabled={!ready}
            onClick={() => {
              setAnchor(null);
              setPrintOpen(true);
            }}
          >
            Download PDF
          </MenuItem>
        )}
        {allowScheduling && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setOpen(true);
            }}
          >
            Schedule email…
          </MenuItem>
        )}
      </Menu>
      <NativePdfSettingsDialog
        open={printOpen}
        ready={ready}
        onClose={() => setPrintOpen(false)}
        onDownload={onDownloadPdf}
      />
      {open && query.data ? (
        <LensSchedulesDialog
          open
          onClose={() => setOpen(false)}
          lens={query.data.lens}
          screenValues={filters}
          filterOptions={filterOptions}
          userEmail={user?.email}
          allowPeriodOverride={pageId !== 'daily-snapshot'}
          initialPeriod={initialPeriod ?? (filters.period as LensPeriod | undefined)}
        />
      ) : (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Schedule email</DialogTitle>
          <DialogContent>
            {query.isError ? (
              <Alert severity="error">
                {query.error?.message ?? 'The report could not load. Check its Library registration and try again.'}
              </Alert>
            ) : (
              <CircularProgress size={24} aria-label="Loading report schedules" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
